import { Navigate, RouteObject } from 'react-router-dom';

import { SystemRoles } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import { RoleItem } from '~hooks/useUsers/models';
import AuthenticatedLayout from '~layout/AuthenticatedLayout';
import UnauthenticatedLayout from '~layout/UnauthenticatedLayout';
import AssignedOrders from '~pages/Assigned/AssignedOrdersProvider';
import { FinalConfirmationScreen } from '~pages/Auth/FinalConfirmationScreen';
import ForgotPassword from '~pages/Auth/ForgotPassword';
import OauthAuthorize from '~pages/Auth/OauthAuthorize';
import ResetPassword from '~pages/Auth/ResetPassword';
import SignIn from '~pages/Auth/SignIn';
import SignUp from '~pages/Auth/SignUp';
import TreadLive from '~pages/LiveMap/TreadLive';
import Messages from '~pages/Messages/Messages';
import { OmniReports } from '~pages/OmniReports/OmniReports';
import Payment from '~pages/Payment';
import Reconciliation from '~pages/Reconciliation';
import Projects from '~pages/Sales/Projects/v2/Projects';
import CompanyOnboardingStepPage from '~pages/Settings/CompanyOnboarding/CompanyOnboardingStepPage';
import CompanyOnboardingWizardHeader from '~pages/Settings/CompanyOnboarding/CompanyOnboardingWizardHeader';
import { RouteErrorElement } from '~pages/Static/InternalErrorPage';
import { PageNotFoundErrorPage } from '~pages/Static/PageNotFoundErrorPage';
import { RouteHandle } from '~router/routeHandle';
import { ROUTES } from '~router/routePaths';
import { hasActiveSession, isImpersonating } from '~services/sessionTokens';
import UserStore from '~store/UserStore';

import {
  ADMIN_WITH_NESTED_ROUTES,
  companyOnboardingRouteElement,
  DISPATCH_WITH_NESTED_ROUTES,
  getApprovalsNestedRoutes,
  getSettingsNestedRoutes,
} from './nestedRoutes';
import { ProtectedRoute } from './ProtectedRoute';
import { PublicRoute } from './PublicRoute';

const publicRoutes: RouteObject[] = [
  {
    path: ROUTES.signUp,
    handle: { titleKey: 'actions.signUp' } satisfies RouteHandle,
    element: <PublicRoute element={SignUp} />,
  },
  {
    path: ROUTES.signIn,
    handle: { titleKey: 'actions.signIn' } satisfies RouteHandle,
    element: <PublicRoute element={SignIn} />,
  },
  {
    path: ROUTES.forgot,
    handle: { titleKey: 'page_headings.forgot_password' } satisfies RouteHandle,
    element: <PublicRoute element={ForgotPassword} />,
  },
  {
    path: ROUTES.reset,
    handle: { titleKey: 'page_headings.reset_password' } satisfies RouteHandle,
    element: <PublicRoute element={ResetPassword} />,
  },
  {
    path: ROUTES.signUp,
    handle: { titleKey: 'actions.signUp' } satisfies RouteHandle,
    element: <PublicRoute element={SignUp} />,
  },
  {
    path: ROUTES.authConfirmation,
    element: <PublicRoute element={FinalConfirmationScreen} />,
  },
];

function DispatchV3ToDispatchRedirect() {
  return <Navigate to={`/${ROUTES.dispatch.base}`} replace />;
}

function SalesProjectsToProjectsRedirect() {
  return <Navigate to={`/${ROUTES.projects}`} replace />;
}

export const getRedirectPathForNonAdmin = (hasDispatchAccess: boolean) => {
  const defaultNonAdminRoute = hasDispatchAccess ? ROUTES.dispatch.base : ROUTES.assigned;
  return defaultNonAdminRoute;
};

export const getRoutes = (userStore: UserStore) => {
  const { isViewOnlyUser } = userStore;
  const isPlatformAdmin = userStore.user.userRoles?.some(
    (role: RoleItem) => role.name === SystemRoles.PLATFORM_ADMIN,
  );
  const canViewReports = userStore.user.userRoles?.some(
    (role: RoleItem) => role.name === SystemRoles.REPORTING,
  );
  const userAccess = userStore.getAccess();
  const defaultNonAdminRoute = getRedirectPathForNonAdmin(userAccess.dispatchTab);

  const redirectPath =
    isPlatformAdmin && !isImpersonating() ? ROUTES.admin.base : defaultNonAdminRoute;

  const appRoutes: RouteObject = {
    path: '/',
    element: <ProtectedRoute element={AuthenticatedLayout} />,
    hasErrorBoundary: true,
    errorElement: <RouteErrorElement />,
    children: [
      {
        /*
        Only redirects when we hit default route via sign in or impersonate.
        To be more explicit, in time, we can conditionally render the base route based on user role.
        */
        index: true,
        element: <Navigate to={redirectPath} replace />,
      },
      isPlatformAdmin && !isImpersonating() ? ADMIN_WITH_NESTED_ROUTES : {},
      !isViewOnlyUser && userAccess.approvalsTab
        ? getApprovalsNestedRoutes(userStore)
        : {},
      !isViewOnlyUser && userAccess.salesTab
        ? {
            path: ROUTES.projects,
            handle: { titleKey: 'page_headings.projects' } satisfies RouteHandle,
            element: (
              <ProtectedRoute element={Projects} canAccess={userAccess.salesTab} />
            ),
          }
        : {},
      getSettingsNestedRoutes(userStore),
      {
        // Outside the Settings outlet, so the wizard step has no Settings sidebar.
        path: `${ROUTES.settings.base}/${ROUTES.settings.companyOnboardingStep}`,
        handle: {
          header: CompanyOnboardingWizardHeader,
          isFullBleed: true,
          titleKey: 'page_headings.settings',
        } satisfies RouteHandle,
        element: companyOnboardingRouteElement(userStore, CompanyOnboardingStepPage),
      },
      {
        path: `${ROUTES.live}/:jobId?`,
        handle: { titleKey: 'page_headings.live_map' } satisfies RouteHandle,
        element: <ProtectedRoute element={TreadLive} canAccess={userAccess.liveMapTab} />,
      },
      {
        // The optional channel id selects a pane, not a page of its own.
        path: `${ROUTES.messages}/:channelId?`,
        handle: { titleKey: 'page_headings.messages' } satisfies RouteHandle,
        element: (
          <ProtectedRoute element={Messages} featureFlag={FeatureFlags.inAppMessaging} />
        ),
      },
      {
        path: ROUTES.reconciliation,
        element: <ProtectedRoute element={Reconciliation} />,
      },
      {
        path: ROUTES.payments,
        element: <ProtectedRoute element={Payment} />,
      },
      {
        path: ROUTES.reports,
        handle: { titleKey: 'page_headings.reports' } satisfies RouteHandle,
        element: <ProtectedRoute element={OmniReports} canAccess={canViewReports} />,
      },
      {
        path: ROUTES.assigned,
        handle: { titleKey: 'page_headings.assigned' } satisfies RouteHandle,
        element: <ProtectedRoute element={AssignedOrders} />,
      },
      userAccess.dispatchTab ? DISPATCH_WITH_NESTED_ROUTES : {},
      // Redirect for those users that bookmarked the Dispatch v3 page
      {
        path: ROUTES.dispatch.v3,
        element: <DispatchV3ToDispatchRedirect />,
      },
      // Redirect for those users that bookmarked the old sales/projects page
      {
        path: 'sales/projects',
        element: <SalesProjectsToProjectsRedirect />,
      },
    ],
  };

  // If the user is logged in, we show a 404 page nested in an AuthenticatedLayout.
  // Otherwise, we just show a 404 page.
  const isLoggedIn = hasActiveSession();
  const pageNotFoundRoute: RouteObject = {
    path: '*',
    element: <PageNotFoundErrorPage isLoggedIn={isLoggedIn} />,
  };
  const catchAllRoute: RouteObject = isLoggedIn
    ? {
        ...appRoutes,
        children: [pageNotFoundRoute],
      }
    : {
        path: '/',
        element: <PublicRoute element={UnauthenticatedLayout} />,
        children: [pageNotFoundRoute],
      };

  const oauthAuthorizeRoute: RouteObject = {
    path: ROUTES.oauthAuthorize,
    element: <ProtectedRoute element={OauthAuthorize} returnAfterSignIn />,
  };

  const allRoutes = [...publicRoutes, oauthAuthorizeRoute, appRoutes, catchAllRoute];

  return allRoutes;
};
